import {
  Box,
  PasswordInput as MantinePasswordInput,
  PasswordInputProps,
  Popover,
  Progress,
} from "@mantine/core";
import { IconCheck, IconEyeCheck, IconEyeOff, IconX } from "@tabler/icons-react";
import { forwardRef, useState } from "react";

import { Text } from "#shared/view/components/Text";

interface Props extends PasswordInputProps {
  error?: string;
  label: string;
}

export const PasswordInput = forwardRef<HTMLInputElement, Props>((field, ref) => {
  const [popoverOpened, setPopoverOpened] = useState(false);
  const [value, setValue] = useState("");
  const checks = requirements.map((requirement, index) => (
    <PasswordRequirement key={index} label={requirement.label} meets={requirement.re.test(value)} />
  ));

  const strength = getStrength(value);
  const color = strength === 100 ? "teal" : strength > 50 ? "yellow" : "red";

  return (
    <Popover
      opened={popoverOpened}
      position="bottom"
      width="target"
      transitionProps={{ transition: "pop" }}
    >
      <Popover.Target>
        <div
          onFocusCapture={() => setPopoverOpened(true)}
          onBlurCapture={() => setPopoverOpened(false)}
        >
          <MantinePasswordInput
            {...field}
            ref={ref}
            withAsterisk
            required
            visibilityToggleIcon={({ reveal }) => (reveal ? <IconEyeOff /> : <IconEyeCheck />)}
            onChange={(event) => {
              setValue(event.currentTarget.value);
              field.onChange?.(event);
            }}
          />
        </div>
      </Popover.Target>
      <Popover.Dropdown>
        <Progress color={color} value={strength} size={5} mb="xs" />
        <PasswordRequirement label="Comporte au moins 8 charactères" meets={value.length > 5} />
        {checks}
      </Popover.Dropdown>
    </Popover>
  );
});

function PasswordRequirement({ meets, label }: { meets: boolean; label: string }) {
  return (
    <Text
      color={meets ? "teal" : "red"}
      style={{ display: "flex", alignItems: "center" }}
      mt={7}
      size="sm"
    >
      {meets ? <IconCheck size="0.9rem" /> : <IconX size="0.9rem" />} <Box ml={10}>{label}</Box>
    </Text>
  );
}

const requirements = [
  { re: /[0-9]/, label: "Comporte au moins un chiffre" },
  { re: /[a-z]/, label: "Comporte au moins une lettre minuscule" },
  { re: /[A-Z]/, label: "Comporte au moins une lettre majuscule" },
  {
    // oxlint-disable-next-line no-useless-escape
    re: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/,
    label: "Comporte au moins un caractère spécial",
  },
];

function getStrength(password: string) {
  let multiplier = password.length > 5 ? 0 : 1;

  requirements.forEach((requirement) => {
    if (!requirement.re.test(password)) {
      multiplier += 1;
    }
  });

  return Math.max(100 - (100 / (requirements.length + 1)) * multiplier, 10);
}
